import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../lib/api';
import { usePublicTheme } from '../../hooks/usePublicTheme';
import { AmbianceBand } from '../Ambiance/AmbianceBand';
import { Modal } from '../ui/Modal';
import { LegalMentions } from '../legal/LegalMentions';
import { PrivacyPolicy } from '../legal/PrivacyPolicy';
import { TermsOfUse } from '../legal/TermsOfUse';
import { DemoRequestModal, usePlatformDemo } from '../Vitrine/DemoRequestModal';
import { BrandLogo, Pill, ThemeSwitch } from '../Vitrine/VitrineUi';
import { Field, Input, PasswordInput } from '../ui/Field';
import { useTurnstile } from './Turnstile';

/**
 * Page de connexion — maquette « Accueil LIAmani » 2b : même langage que la vitrine, rien de
 * plus que nécessaire. Sortie de App.tsx le 15/09 avec l'accord d'Emeni ; la logique de
 * connexion (lecture par FormData pour l'auto-remplissage iOS, redirections par type de compte,
 * retour vers l'URL demandée) est reprise à l'identique.
 * « Mot de passe oublié » mène à /mot-de-passe-oublie (ajouté le 13/10). Pas de « demander un accès » :
 * ce parcours n'existe pas encore dans l'app, on ne promet rien qui n'a pas d'écran.
 */
export const LoginPage: React.FC = () => {
    const { isDark, toggleTheme } = usePublicTheme();
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // URL demandée avant d'être redirigé vers /login (ex: lien de booking d'extras avec ?bookEvent=...),
    // posée par Layout — uniquement un chemin interne (jamais fourni par une source externe), donc sûr à
    // réutiliser tel quel. Demande d'Emeni (2026-08-22).
    const fromPath = (location.state as any)?.from;
    const safeFromPath = typeof fromPath === 'string' && fromPath.startsWith('/') && !fromPath.startsWith('//') ? fromPath : null;

    useEffect(() => {
        if (user) navigate(safeFromPath || '/');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, navigate]);

    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [legal, setLegal] = useState<'mentions' | 'privacy' | 'terms' | null>(null);
    const demo = usePlatformDemo();
    // Filtre anti-robots de Cloudflare. Muet tant que les cles ne sont pas posees sur l'environnement.
    const robot = useTurnstile();
    const [demoOpen, setDemoOpen] = useState(false);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            // Lit les valeurs directement depuis le DOM (via FormData) plutôt que le seul state React :
            // sur iOS Safari, l'auto-remplissage (mots de passe enregistrés/Trousseau) met parfois à jour
            // visuellement le champ sans déclencher l'événement onChange de React, laissant le state
            // désynchronisé de ce qui est réellement affiché/saisi.
            const formData = new FormData(e.currentTarget);
            const submittedIdentifier = String(formData.get('identifier') || identifier).trim();
            const submittedPassword = String(formData.get('password') || password);
            const data = await authApi.login({ identifier: submittedIdentifier, password: submittedPassword, resterConnecte: formData.get('resterConnecte') === 'on', turnstileToken: robot.jeton });
            login(data.token, data.user);
            if (data.user?.type === 'employe') {
                navigate(safeFromPath || (data.user?.has_pending_onboarding ? '/mon-integration' : '/planning'));
            } else {
                navigate(safeFromPath || '/');
            }
        } catch (err: any) {
            setError(err.message);
            // Le jeton vient d'etre consomme par la tentative : sans ce renouvellement, un second essai
            // apres une faute de frappe partirait avec un jeton mort et serait refuse a tort.
            robot.reinitialiser();
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <AmbianceBand veil="rgba(5,6,10,.22)" className="min-h-screen flex flex-col" style={{ fontFamily: '"Instrument Sans", ui-sans-serif, system-ui, sans-serif' }}>
            <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10"><ThemeSwitch isDark={isDark} onToggle={toggleTheme} /></div>
            <div className="flex-1 flex flex-col items-center justify-center px-4 py-10">
                <a href="/" className="mb-8"><BrandLogo className="h-7" /></a>

                <div className="w-full max-w-[400px] rounded-2xl border border-border bg-surface shadow-2xl p-7 sm:p-8">
                    <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.02em] text-content">Bon retour.</h1>
                    <p className="text-[13.5px] text-content-muted mt-1.5 mb-6">Entrez vos identifiants pour accéder à votre espace.</p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <div role="alert" className="flex items-start gap-2 p-3 rounded-lg text-[13px] bg-state-danger-soft text-state-danger">
                                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                                <span>{error}</span>
                            </div>
                        )}
                        <Field label="Identifiant">
                            {({ id }) => (
                                <Input
                                    id={id}
                                    type="text"
                                    name="identifier"
                                    value={identifier}
                                    onChange={(e) => setIdentifier(e.target.value)}
                                    placeholder="Email ou pseudo"
                                    required
                                    autoComplete="username"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    autoFocus
                                />
                            )}
                        </Field>
                        <Field label="Mot de passe">
                            {({ id }) => (
                                <PasswordInput
                                    id={id}
                                    name="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    autoComplete="current-password"
                                />
                            )}
                        </Field>
                        <div className="flex items-center justify-between gap-3 -mt-1">
                            <label className="flex items-center gap-2 text-[13px] text-content-muted cursor-pointer select-none">
                                <input type="checkbox" name="resterConnecte"className="h-4 w-4 rounded border-border accent-[var(--brand-color)]" />
                                Rester connecté
                            </label>
                            <Link to="/mot-de-passe-oublie" className="text-[13px] text-content-muted hover:text-content underline underline-offset-4">Mot de passe oublié ?</Link>
                        </div>
                        {robot.rendu}
                        <Pill type="submit" size="lg" disabled={submitting || robot.enAttente} className="w-full mt-2 disabled:opacity-60">
                            {submitting ? 'Connexion…' : robot.enAttente ? 'Vérification…' : 'Se connecter'}
                        </Pill>
                    </form>

                    {demo.clientId && (
                        <>
                            <div className="flex items-center gap-3 my-6 text-[11px] uppercase tracking-[.09em] text-content-muted">
                                <span className="flex-1 h-px bg-border" />Pas encore de compte ?<span className="flex-1 h-px bg-border" />
                            </div>
                            <Pill variant="secondary" className="w-full" onClick={() => setDemoOpen(true)}>{demo.label}</Pill>
                        </>
                    )}
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[12px] text-content-muted">
                    <span>Accès réservé aux clients, collaborateurs et salariés autorisés.</span>
                    <button type="button" onClick={() => setLegal('mentions')} className="hover:text-content">Mentions légales</button>
                    <button type="button" onClick={() => setLegal('privacy')} className="hover:text-content">Confidentialité</button>
                    <button type="button" onClick={() => setLegal('terms')} className="hover:text-content">Conditions d'utilisation</button>
                </div>
            </div>

            <Modal open={legal === 'mentions'} onClose={() => setLegal(null)} size="lg" title="Mentions légales">
                <div className="text-[13px] text-content-muted"><LegalMentions /></div>
            </Modal>
            <Modal open={legal === 'privacy'} onClose={() => setLegal(null)} size="lg" title="Politique de confidentialité">
                <div className="text-[13px] text-content-muted"><PrivacyPolicy /></div>
            </Modal>
            <Modal open={legal === 'terms'} onClose={() => setLegal(null)} size="xl" title="Conditions d'utilisation">
                <div className="text-[13px] text-content-muted"><TermsOfUse /></div>
            </Modal>
            {demo.clientId && <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} clientId={demo.clientId} title={demo.label} />}
        </AmbianceBand>
    );
};
